import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { firestore } from "../firebase";
import { mapDocToProject, Project, ProjectCollection } from "../types/Project";
import { StripeConnectInfoCollection } from "../types/StripeConnectInfo";

export async function createProject(
  creatorUserId: string,
  name: string,
  description: string,
): Promise<Project> {
  const now = Timestamp.now();
  const projectData = {
    creatorUserId,
    name,
    description,
    createdAt: now,
    updatedAt: now,
    startupStatus: [],
  };
  const docRef = await addDoc(
    collection(firestore, ProjectCollection),
    projectData,
  );
  return mapDocToProject(docRef.id, projectData);
}

export async function getProject(projectId: string): Promise<Project | null> {
  const projectDocRef = doc(firestore, ProjectCollection, projectId);
  const projectDocSnap = await getDoc(projectDocRef);

  if (projectDocSnap.exists()) {
    const data = projectDocSnap.data();
    return mapDocToProject(projectDocSnap.id, data);
  }
  return null;
}

export async function getUserProjects(userId: string): Promise<Project[]> {
  const projectsQuery = query(
    collection(firestore, ProjectCollection),
    where("creatorUserId", "==", userId),
  );
  const querySnapshot = await getDocs(projectsQuery);
  return querySnapshot.docs.map((doc) => mapDocToProject(doc.id, doc.data()));
}

export async function updateProject(
  projectId: string,
  updates: Partial<Project>,
): Promise<void> {
  const projectDocRef = doc(firestore, ProjectCollection, projectId);
  await updateDoc(projectDocRef, { ...updates, updatedAt: Timestamp.now() });
}

export async function deleteProject(projectId: string): Promise<void> {
  const projectDocRef = doc(firestore, ProjectCollection, projectId);
  await deleteDoc(projectDocRef);
}

export async function saveStripeConnectInfo(
  projectId: string,
  code: string,
  scope: string,
): Promise<void> {
  const projectDocRef = doc(firestore, ProjectCollection, projectId);
  await updateDoc(projectDocRef, {
    stripeConnectCode: code,
    stripeConnectScope: scope,
    stripeConnectTimestamp: Timestamp.now(),
  });
}

export async function disconnectFromStripe(projectId: string): Promise<void> {
  // Create a transaction to update the project and the stripe-connections collection
  const transaction = writeBatch(firestore);

  // Update the project
  const projectDocRef = doc(firestore, ProjectCollection, projectId);
  transaction.update(projectDocRef, {
    stripeConnectCode: null,
    stripeConnectScope: null,
    stripeConnectTimestamp: null,
  });

  // Delete the stripe-connections collection
  const stripeConnectionsCollectionRef = doc(
    firestore,
    StripeConnectInfoCollection,
    projectId,
  );
  transaction.delete(stripeConnectionsCollectionRef);

  await transaction.commit();
}
